import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import { stErrorMessage, stSignalEvents, stSignalStats, StSignalEvent, StSignalStats } from '../../../services/socialTradingService';
import { StIdCell, StScrollTable, StStatusChip, stFormatDateTime, stText } from '../StListUi';

const PAGE_SIZE = 20;

type SignalFilters = {
  status?: string;
  signalId?: string;
  agentId?: string;
  roomId?: string;
};

const PUBLISH_STATUSES = ['PUBLISHED', 'FAILED', 'SKIPPED'] as const;

export default function SocialTradingSignalsList() {
  const t = useTranslate();
  const notify = useNotify();
  const [stats, setStats] = useState<StSignalStats | null>(null);
  const [events, setEvents] = useState<StSignalEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusInput, setStatusInput] = useState('');
  const [signalIdInput, setSignalIdInput] = useState('');
  const [agentIdInput, setAgentIdInput] = useState('');
  const [roomIdInput, setRoomIdInput] = useState('');
  const [applied, setApplied] = useState<SignalFilters>({});

  const applyFilters = useCallback(() => {
    setApplied({
      status: statusInput.trim() || undefined,
      signalId: signalIdInput.trim() || undefined,
      agentId: agentIdInput.trim() || undefined,
      roomId: roomIdInput.trim() || undefined,
    });
    setPage(0);
  }, [statusInput, signalIdInput, agentIdInput, roomIdInput]);

  const load = useCallback(async () => {
    try {
      const [st, ev] = await Promise.all([
        stSignalStats(),
        stSignalEvents({ ...applied, limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
      ]);
      setStats(st);
      setEvents(ev?.items || []);
      setTotal(ev?.total || 0);
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [page, applied, notify]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PermissionGuard resource="st-signals" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.signals')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.signals') },
          ]}
          action={
            <Button size="small" variant="outlined" onClick={load}>
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {[
              { title: t('module.socialTrading.signalReceivedToday'), value: stats?.receivedToday },
              { title: t('module.socialTrading.signalPublishedToday'), value: stats?.publishedToday },
              { title: t('module.socialTrading.signalFailedToday'), value: stats?.failedToday },
              { title: t('module.socialTrading.signalSkippedToday'), value: stats?.skippedToday },
            ].map((k) => (
              <Grid item xs={12} md={3} key={k.title}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">
                      {k.title}
                    </Typography>
                    <Typography variant="h5">{k.value ?? '-'}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('module.socialTrading.signalLogs')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" useFlexGap flexWrap="wrap">
                <TextField
                  select
                  size="small"
                  label={t('module.socialTrading.colPublishStatus')}
                  value={statusInput}
                  onChange={(e) => setStatusInput(e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="">{t('module.socialTrading.filterAll')}</MenuItem>
                  {PUBLISH_STATUSES.map((id) => (
                    <MenuItem key={id} value={id}>
                      {t(`module.socialTrading.publishStatus.${id}`)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label={t('module.socialTrading.filterSignalId')}
                  value={signalIdInput}
                  onChange={(e) => setSignalIdInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyFilters();
                    }
                  }}
                  sx={{ minWidth: 180 }}
                />
                <TextField
                  size="small"
                  label={t('module.socialTrading.filterAgentId')}
                  value={agentIdInput}
                  onChange={(e) => setAgentIdInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyFilters();
                    }
                  }}
                  sx={{ minWidth: 180 }}
                />
                <TextField
                  size="small"
                  label={t('module.socialTrading.filterRoomId')}
                  value={roomIdInput}
                  onChange={(e) => setRoomIdInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      applyFilters();
                    }
                  }}
                  sx={{ minWidth: 180 }}
                />
                <Button size="small" variant="contained" onClick={applyFilters}>
                  {t('module.socialTrading.applyFilters')}
                </Button>
              </Stack>
              <StScrollTable>
                <TableHead>
                  <TableRow>
                    <TableCell>signalId</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>strategyId</TableCell>
                    <TableCell>{t('module.socialTrading.colRoom')}</TableCell>
                    <TableCell>{t('module.socialTrading.colSymbol')}</TableCell>
                    <TableCell>{t('module.socialTrading.colSide')}</TableCell>
                    <TableCell>{t('module.socialTrading.colPublishStatus')}</TableCell>
                    <TableCell>{t('module.socialTrading.colSignalStatus')}</TableCell>
                    <TableCell>{t('module.socialTrading.colValidMinutes')}</TableCell>
                    <TableCell>{t('module.socialTrading.colExpiresAt')}</TableCell>
                    <TableCell>{t('module.socialTrading.colFailureReason')}</TableCell>
                    <TableCell>{t('module.socialTrading.colTime')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {events.map((ev) => (
                    <TableRow key={ev.id}>
                      <TableCell><StIdCell value={ev.signalId} /></TableCell>
                      <TableCell>{stText(ev.agentName)}</TableCell>
                      <TableCell>{stText(ev.strategyId)}</TableCell>
                      <TableCell>{stText(ev.roomName || ev.roomId || '')}</TableCell>
                      <TableCell>{stText(ev.symbol)}</TableCell>
                      <TableCell>{stText(ev.side)}</TableCell>
                      <TableCell>
                        <StStatusChip status={ev.status} />
                      </TableCell>
                      <TableCell>
                        {ev.signalStatus === 'ACTIVE' || ev.signalStatus === 'EXPIRED' ? (
                          <Chip
                            size="small"
                            color={ev.signalStatus === 'EXPIRED' ? 'error' : 'success'}
                            label={t(
                              ev.signalStatus === 'EXPIRED'
                                ? 'module.socialTrading.signalStatusExpired'
                                : 'module.socialTrading.signalStatusActive'
                            )}
                          />
                        ) : (
                          stText('')
                        )}
                      </TableCell>
                      <TableCell>{stText(ev.validMinutes ?? '')}</TableCell>
                      <TableCell>{stText(stFormatDateTime(ev.expiresAt))}</TableCell>
                      <TableCell>{stText(ev.failureReason)}</TableCell>
                      <TableCell>{stText(stFormatDateTime(ev.createdAt))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </StScrollTable>

              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={PAGE_SIZE}
                rowsPerPageOptions={[PAGE_SIZE]}
              />
            </CardContent>
          </Card>
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
}
